import { type FormEvent, useEffect, useState } from "react"
import { toast } from "sonner"
import { type CurrentUser, type MailSettings, api } from "@/lib/api"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { AtSign, KeyRound, Mail, Save } from "lucide-react"

type Props = {
  currentUser: CurrentUser
}

export function MailSettingsPage({ currentUser }: Props) {
  const [mail, setMail] = useState<MailSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fromName, setFromName] = useState("")
  const [fromEmail, setFromEmail] = useState("")
  const [newApiKey, setNewApiKey] = useState("")

  const isAdmin = currentUser.permissions.includes("admin")

  useEffect(() => {
    if (!isAdmin) return
    api
      .getMailSettings()
      .then((res) => {
        setMail(res.mail)
        setFromName(res.mail.from_name)
        setFromEmail(res.mail.from_email)
      })
      .catch(() => toast.error("Erro ao carregar configuração de e-mail."))
      .finally(() => setLoading(false))
  }, [isAdmin])

  const submitDisabled =
    saving ||
    loading ||
    !fromEmail.trim() ||
    (!mail?.resend_api_key_configured && !newApiKey.trim())

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitDisabled) return

    setSaving(true)
    try {
      const payload = {
        from_name: fromName.trim(),
        from_email: fromEmail.trim(),
        ...(newApiKey.trim() ? { resend_api_key: newApiKey.trim() } : {}),
      }
      const res = await api.updateMailSettings(payload)
      setMail(res.mail)
      setFromName(res.mail.from_name)
      setFromEmail(res.mail.from_email)
      setNewApiKey("")
      toast.success(res.log)
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Erro ao salvar configuração de e-mail."
      )
    } finally {
      setSaving(false)
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex w-full flex-1 flex-col items-start justify-start p-4 sm:p-6 md:p-8">
        <p className="text-sm text-muted-foreground">
          Sem permissão para acessar esta seção.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto p-4 sm:p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">
            Configuração de e-mail
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure o remetente usado para enviar acessos VPN por e-mail.
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.8fr)]">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-medium">
                  Dados do remetente
                </CardTitle>
                <CardDescription className="text-xs">
                  A API key atual nunca é exibida sem máscara.
                </CardDescription>
              </div>
              <Mail className="size-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="mail-from-name">Nome do remetente</Label>
                  <Input
                    id="mail-from-name"
                    value={fromName}
                    onChange={(event) => setFromName(event.target.value)}
                    placeholder="VPN"
                    disabled={saving}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="mail-from-email">E-mail remetente *</Label>
                  <Input
                    id="mail-from-email"
                    type="email"
                    value={fromEmail}
                    onChange={(event) => setFromEmail(event.target.value)}
                    placeholder="vpn@seudominio.com"
                    disabled={saving}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="mail-resend-key">Nova API key do Resend</Label>
                  <Input
                    id="mail-resend-key"
                    type="password"
                    value={newApiKey}
                    onChange={(event) => setNewApiKey(event.target.value)}
                    placeholder={
                      mail?.resend_api_key_configured
                        ? "Deixe em branco para manter a key atual"
                        : "Informe a API key do Resend"
                    }
                    disabled={saving}
                    autoComplete="new-password"
                  />
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={submitDisabled}>
                    <Save />
                    {saving ? "Salvando..." : "Salvar configuração"}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-medium">
                  Configuração atual
                </CardTitle>
                <CardDescription className="text-xs">
                  Valores salvos em mail_settings.
                </CardDescription>
              </div>
              <KeyRound className="size-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-3">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="mt-1 text-sm font-medium">
                      {mail?.resend_api_key_configured
                        ? "Pronto para envio"
                        : "Pendente de configuração"}
                    </p>
                  </div>
                  <Badge
                    variant={
                      mail?.resend_api_key_configured ? "default" : "secondary"
                    }
                    className="shrink-0"
                  >
                    {mail?.resend_api_key_configured ? "ativo" : "incompleto"}
                  </Badge>
                </div>

                <div className="rounded-md border bg-muted/20 p-3">
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <AtSign className="size-3.5" />
                    Remetente
                  </p>
                  <p className="mt-1 truncate text-sm font-medium">
                    {mail?.from_name || "VPN"}
                  </p>
                  <p className="truncate font-mono text-[11px] text-muted-foreground">
                    {mail?.from_email || "-"}
                  </p>
                </div>

                <div className="rounded-md border bg-muted/20 p-3">
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <KeyRound className="size-3.5" />
                    Resend API key
                  </p>
                  <p className="mt-1 break-all font-mono text-xs">
                    {mail?.resend_api_key_masked || "Não configurada"}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
